import { useState, useRef, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Button } from '@lib/ui';

export interface CarouselProps {
  /** Array of items to display */
  children: ReactNode[];
  /** Number of items to show at once */
  slidesToShow?: number;
  /** Gap between slides in pixels */
  gap?: number;
  /** Whether to show navigation arrows */
  showArrows?: boolean;
  /** Whether to show navigation dots */
  showDots?: boolean;
  /** Whether to auto-play */
  autoPlay?: boolean;
  /** Auto-play interval in milliseconds */
  autoPlayInterval?: number;
  /** Whether to loop infinitely */
  loop?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Additional className for the slides container */
  slidesClassName?: string;
}

const ChevronLeftIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="size-5"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="size-5"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

export function Carousel({
  children,
  slidesToShow = 1,
  gap = 16,
  showArrows = true,
  showDots = true,
  autoPlay = false,
  autoPlayInterval = 5000,
  loop = false,
  className,
  slidesClassName,
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const totalSlides = children.length;
  const maxIndex = Math.max(0, totalSlides - slidesToShow);

  const goToSlide = useCallback(
    (index: number) => {
      if (loop) {
        if (index < 0) {
          setCurrentIndex(maxIndex);
        } else if (index > maxIndex) {
          setCurrentIndex(0);
        } else {
          setCurrentIndex(index);
        }
      } else {
        setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
      }
    },
    [loop, maxIndex]
  );

  const goToPrevious = useCallback(() => {
    goToSlide(currentIndex - 1);
  }, [currentIndex, goToSlide]);

  const goToNext = useCallback(() => {
    goToSlide(currentIndex + 1);
  }, [currentIndex, goToSlide]);

  // Auto-play effect
  useEffect(() => {
    if (!autoPlay || isHovering) return;

    const interval = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, goToNext, isHovering]);

  const canGoPrevious = loop || currentIndex > 0;
  const canGoNext = loop || currentIndex < maxIndex;

  return (
    <div
      className={clsx('relative', className)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Slides container */}
      <div ref={containerRef} className="overflow-hidden">
        <div
          className={clsx('flex transition-transform duration-300 ease-out', slidesClassName)}
          style={{
            gap: `${gap}px`,
            transform: `translateX(calc(-${currentIndex * (100 / slidesToShow)}% - ${currentIndex * (gap / slidesToShow)}px))`,
          }}
        >
          {children.map((child, index) => (
            <div
              key={index}
              className="shrink-0"
              style={{
                width: `calc((100% - ${(slidesToShow - 1) * gap}px) / ${slidesToShow})`,
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows */}
      {showArrows && totalSlides > slidesToShow && (
        <>
          <Button
            variant="flat"
            isIcon
            className={clsx(
              'absolute left-2 top-1/2 -translate-y-1/2 size-10 rounded-full bg-white/90 shadow-md dark:bg-dark-700/90',
              !canGoPrevious && 'opacity-50 cursor-not-allowed'
            )}
            onClick={goToPrevious}
            disabled={!canGoPrevious}
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="flat"
            isIcon
            className={clsx(
              'absolute right-2 top-1/2 -translate-y-1/2 size-10 rounded-full bg-white/90 shadow-md dark:bg-dark-700/90',
              !canGoNext && 'opacity-50 cursor-not-allowed'
            )}
            onClick={goToNext}
            disabled={!canGoNext}
          >
            <ChevronRightIcon />
          </Button>
        </>
      )}

      {/* Navigation dots */}
      {showDots && totalSlides > slidesToShow && (
        <div className="mt-4 flex justify-center gap-2">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={clsx(
                'size-2 rounded-full transition-all',
                index === currentIndex
                  ? 'bg-primary-500 w-6'
                  : 'bg-gray-300 dark:bg-dark-500 hover:bg-gray-400 dark:hover:bg-dark-400'
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

Carousel.displayName = 'Carousel';
